/**
 * A course's mark.
 *
 * At the kit sizes (`sm`, 20 px in registers and rail rows; `lg`, 40 px in a
 * glance tile) the mark is the generated signature alone: the hue's tint as the
 * fill, the motif glyph in the hue (ADR 0035). Without a size it is the legacy
 * footprint: the course's own image when it has one that loads, otherwise the
 * signature tile. The signature always renders first and the image fades in
 * over it once loaded, so a slow image shows the signature rather than an
 * empty box, and a broken one never shows at all. The server sends only HTTPS
 * image URLs, so the client applies no URL rule of its own. Both are
 * decorative: the code and title beside the mark carry the accessible identity.
 */
import { useState } from "react";

import { CourseGlyph } from "@/components/workspace/CourseGlyph";
import { CourseSignature } from "@/components/workspace/CourseSignature";
import { COURSE_HUE, COURSE_TINT, courseSignature } from "@/lib/course-signature";
import { cn } from "@/lib/utils";

export type CourseImageSettled = { url: string; state: "loaded" | "failed" };

export type CourseMarkSize = "sm" | "lg";

const MARK_SIZES: Record<CourseMarkSize, { box: string; glyph: string }> = {
  sm: { box: "size-5 rounded-[0.375rem]", glyph: "size-3" },
  lg: { box: "size-10 rounded-[0.625rem]", glyph: "size-[1.375rem]" },
};

function SignatureMark({ code, size, className }: { code: string; size: CourseMarkSize; className?: string }) {
  const { hue, motif } = courseSignature(code);
  return (
    <span
      aria-hidden="true"
      data-course-signature={`${hue}:${motif}`}
      data-course-mark={size}
      className={cn("inline-grid shrink-0 place-items-center", MARK_SIZES[size].box, className)}
      style={{ background: COURSE_TINT[hue], color: COURSE_HUE[hue] }}
    >
      <CourseGlyph motif={motif} className={MARK_SIZES[size].glyph} />
    </span>
  );
}

function ImageMark({
  code,
  imageUrl,
  className,
  onImageSettled,
}: {
  code: string;
  imageUrl: string | null;
  className?: string;
  onImageSettled?: (settled: CourseImageSettled) => void;
}) {
  // Keyed by URL, so a refetch that changes the image starts loading again.
  const [settled, setSettled] = useState<CourseImageSettled | null>(null);
  const state = imageUrl && settled?.url === imageUrl ? settled.state : "loading";
  const loaded = state === "loaded";
  const settle = (next: CourseImageSettled) => {
    setSettled(next);
    onImageSettled?.(next);
  };
  return (
    <span className={cn("relative block size-14 shrink-0 sm:size-16", className)}>
      <CourseSignature code={code} className={cn("size-full sm:size-full", loaded && "invisible")} />
      {imageUrl && state !== "failed" && (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          draggable={false}
          onLoad={() => settle({ url: imageUrl, state: "loaded" })}
          onError={() => settle({ url: imageUrl, state: "failed" })}
          data-course-image={state}
          // 18.75% is the signature tile's 12/64 corner, so both shapes match
          // at every size.
          className={cn(
            "absolute inset-0 size-full rounded-[18.75%] bg-card object-cover outline-1 -outline-offset-1 outline-border",
            "transition-opacity duration-[var(--motion-normal)] ease-[var(--ease-standard)]",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </span>
  );
}

export function CourseMark({
  code,
  imageUrl = null,
  size,
  className,
  onImageSettled,
}: {
  code: string;
  /** Read only by the legacy footprint; the kit sizes draw the signature alone. */
  imageUrl?: string | null;
  size?: CourseMarkSize;
  className?: string;
  /** Reports how the image ended, so an editor can say a link did not load. */
  onImageSettled?: (settled: CourseImageSettled) => void;
}) {
  if (size) return <SignatureMark code={code} size={size} className={className} />;
  return <ImageMark code={code} imageUrl={imageUrl} className={className} onImageSettled={onImageSettled} />;
}
